var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { createSlice } from '@reduxjs/toolkit';
import { t } from '../../strings/i18n/i18n';
import { ADDITIONAL_INFO_COMMENT_CHARACTER_LIMIT, ALLOWED_SPECIAL_CHARS, specialCharacterRegex } from '../../const/characterValidation';
import { Analytics } from '../../helpers/analyticsMiddleware';
import { triggerAnalyticsEvent } from 'syf-js-utilities';
import fileUploadConfig from '../../const/fileUploadConfig';
import AdditionalInfoErrorCodes from '../../const/AdditionalInfoErrorCodes';
import { AdditionalInfoView } from './typings';
import submitFilesAndComments from './submitFilesAndComments';
import { acceptedFileToRejectedFile, createAcceptedFile, getAdditionalInfoAnalyticsEventOptions, getRejectedFilesErrors, getTotalFileSize } from './helpers';
export var initialState = {
    acceptedFiles: [],
    currentView: AdditionalInfoView.Upload,
    rejectedFiles: [],
    rejectedFilesErrors: [],
    userComment: '',
    userCommentError: '',
    maxFilesReached: false
};
var maxFiles = fileUploadConfig.maxFiles, maxUploadFileSize = fileUploadConfig.maxUploadFileSize, maxFileSize = fileUploadConfig.maxFileSize;
var additionalInfo = createSlice({
    name: 'additionalInfo',
    initialState: initialState,
    reducers: {
        updateSelectedFile: function (state, action) {
            state.selectedFile = action.payload;
        },
        deleteSelectedFile: function (state) {
            var selectedFile = state.selectedFile, rejectedFiles = state.rejectedFiles, acceptedFiles = state.acceptedFiles;
            if ('errors' in selectedFile) {
                var selectedFileIndex = rejectedFiles
                    .map(function (rejectedFile) { return rejectedFile.file.name; })
                    .indexOf(selectedFile.file.name);
                state.rejectedFiles.splice(selectedFileIndex, 1);
                // Convert back to accepted files the files that were marked as rejected due to max upload size reached
                var sizeErrorFiles = state.rejectedFiles.filter(function (_a) {
                    var errors = _a.errors;
                    // Filter out the rejected files from the partial upload scenario that don't necessarily have an error attached
                    if (!errors.length)
                        return false;
                    var code = errors[0].code;
                    return code === AdditionalInfoErrorCodes.FileTooLarge;
                });
                var totalFileSize = getTotalFileSize(sizeErrorFiles);
                if (totalFileSize <= maxUploadFileSize) {
                    state.acceptedFiles = __spreadArray(__spreadArray([], acceptedFiles, true), sizeErrorFiles.map(function (_a) {
                        var file = _a.file;
                        return createAcceptedFile(file);
                    }), true);
                    state.rejectedFiles = state.rejectedFiles.filter(function (_a) {
                        var errors = _a.errors;
                        // Preserve failed files from partial upload scenario as rejected
                        if (!errors.length)
                            return true;
                        var code = errors[0].code;
                        return code !== AdditionalInfoErrorCodes.FileTooLarge;
                    });
                }
                state.rejectedFilesErrors = Object.values(getRejectedFilesErrors(state.rejectedFiles));
            }
            else {
                var selectedFileIndex = acceptedFiles
                    .map(function (acceptedFile) { return acceptedFile.name; })
                    .indexOf(selectedFile.name);
                state.acceptedFiles.splice(selectedFileIndex, 1);
            }
            var fileCount = state.acceptedFiles.length;
            if (fileCount < maxFiles) {
                state.maxFilesReached = false;
            }
            state.selectedFile = null;
            state.currentView = AdditionalInfoView.Upload;
            state.apiError = null;
        },
        updateAcceptedFiles: function (state, action) {
            var rejectedFiles = state.rejectedFiles;
            var acceptedFiles = __spreadArray(__spreadArray([], state.acceptedFiles, true), action.payload.map(function (file) { return createAcceptedFile(file); }), true);
            var fileCount = acceptedFiles.length;
            if (fileCount >= maxFiles) {
                state.maxFilesReached = true;
                acceptedFiles = acceptedFiles.slice(0, maxFiles);
                var errorMessage = t('uploadDocuments.maxAttachmentsReached', {
                    maxAttachments: maxFiles
                });
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageSubFunction: Analytics.MaxAttachments,
                    pageName: Analytics.LimitError,
                    errorMessage: errorMessage
                });
            }
            var notSubmittedFiles = acceptedFiles.filter(function (_a) {
                var alreadySubmitted = _a.alreadySubmitted;
                return !alreadySubmitted;
            });
            /*
             * When checking max file size, we only want to count files that haven't been already submitted
             * and already rejected files due to max size reached by themselves
             */
            var totalFileSize = getTotalFileSize(__spreadArray(__spreadArray([], notSubmittedFiles, true), rejectedFiles.filter(function (_a) {
                var errors = _a.errors;
                if (!errors.length)
                    return false;
                return errors[0].code === AdditionalInfoErrorCodes.FileTooLarge;
            }), true));
            // Convert accepted files to rejected files in case max upload size has been reached
            if (totalFileSize > maxUploadFileSize) {
                var errorMessage_1 = t('additionalInfo.maximumFileSize', {
                    maxFileSize: maxFileSize
                });
                var sizeErrorFiles = notSubmittedFiles.map(function (acceptedFile) {
                    return acceptedFileToRejectedFile(acceptedFile, [
                        {
                            code: AdditionalInfoErrorCodes.FileTooLarge,
                            message: errorMessage_1
                        }
                    ]);
                });
                var submittedFiles = acceptedFiles.filter(function (_a) {
                    var alreadySubmitted = _a.alreadySubmitted;
                    return alreadySubmitted;
                });
                state.rejectedFiles = rejectedFiles.concat(sizeErrorFiles);
                state.rejectedFilesErrors = Object.values(getRejectedFilesErrors(state.rejectedFiles));
                // Remove the accepted files that were converted, but preserve already submitted files in case of partial upload
                state.acceptedFiles = submittedFiles;
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageName: Analytics.LimitError,
                    errorMessage: errorMessage_1
                });
            }
            else {
                state.acceptedFiles = acceptedFiles;
            }
        },
        updateRejectedFiles: function (state, action) {
            state.rejectedFiles = state.rejectedFiles.concat(action.payload);
            var errorMap = getRejectedFilesErrors(state.rejectedFiles);
            state.rejectedFilesErrors = Object.values(errorMap);
            Object.keys(errorMap).forEach(function (errorCode) {
                var _a;
                var analyticsErrors = (_a = {},
                    _a[AdditionalInfoErrorCodes.InvalidFileName] = 'error_char',
                    _a[AdditionalInfoErrorCodes.FileInvalidType] = 'error_unsupported',
                    _a[AdditionalInfoErrorCodes.FileNameTooLong] = 'error_filename',
                    _a);
                var pageName = analyticsErrors[errorCode];
                if (!pageName)
                    return;
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageSubFunction: Analytics.Upload_Documents,
                    pageName: pageName,
                    errorMessage: errorMap[errorCode]
                });
            });
        },
        updateUserComment: function (state, action) {
            var payload = action.payload;
            var userComment = payload.userComment;
            state.userComment = userComment;
            var characterCount = userComment.length;
            var disallowedSpecialCharFound = specialCharacterRegex.test(userComment);
            if (disallowedSpecialCharFound) {
                state.userCommentError = t('additionalInfo.reminder', {
                    specialCharacters: ALLOWED_SPECIAL_CHARS
                });
            }
            else if (characterCount > ADDITIONAL_INFO_COMMENT_CHARACTER_LIMIT) {
                var charsOverLimit = characterCount - ADDITIONAL_INFO_COMMENT_CHARACTER_LIMIT;
                state.userCommentError =
                    charsOverLimit === 1
                        ? "".concat(t('formError.oneCharacterOverLimit'))
                        : "".concat(t('formError.charactersOverLimit', {
                            charsOverLimit: charsOverLimit
                        }));
            }
            else {
                state.userCommentError = '';
            }
            if (state.userCommentError) {
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageName: Analytics.Error,
                    errorMessage: state.userCommentError
                });
            }
        },
        updateAdditionalInfoView: function (state, action) {
            var payload = action.payload;
            var currentView = payload.currentView;
            var acceptedFiles = state.acceptedFiles, userComment = state.userComment;
            state.currentView = currentView;
            var eventOptions = getAdditionalInfoAnalyticsEventOptions(currentView, acceptedFiles, userComment);
            triggerAnalyticsEvent(eventOptions);
        },
        clearAdditionalInfoState: function () {
            return initialState;
        }
    },
    extraReducers: function (builder) {
        builder.addCase(submitFilesAndComments.pending, function () {
            triggerAnalyticsEvent({
                pageKind: Analytics.Dispute,
                pageFunction: Analytics.AdditionalInfo,
                pageSubFunction: Analytics.Upload_Documents,
                pageName: Analytics.SecurityScan
            });
        });
        builder.addCase(submitFilesAndComments.rejected, function (state, action) {
            var payload = action.payload;
            var errorCode = payload.errorCode;
            switch (errorCode) {
                case '600023':
                case '700008':
                    state.apiError = t('additionalInfo.commentServiceError');
                    break;
                default:
                    state.apiError = t('additionalInfo.serviceError');
            }
            if (state.apiError) {
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageName: Analytics.UploadFailure,
                    errorMessage: state.apiError
                });
            }
            state.currentView = AdditionalInfoView.Upload;
        });
        builder.addCase(submitFilesAndComments.fulfilled, function (state, action) {
            var acceptedFiles = state.acceptedFiles, userComment = state.userComment;
            var attachments = action.payload.attachments;
            var submittedFiles = [];
            var failedFiles = [];
            attachments.forEach(function (_a) {
                var fileName = _a.fileName, error = _a.error;
                if (error) {
                    failedFiles.push(fileName);
                }
                else {
                    submittedFiles.push(fileName);
                }
            });
            if (failedFiles.length) {
                state.apiError = t('additionalInfo.partialUpload');
                state.rejectedFiles = acceptedFiles
                    .filter(function (_a) {
                    var name = _a.name;
                    return failedFiles.includes(name);
                })
                    .map(function (acceptedFile) { return acceptedFileToRejectedFile(acceptedFile); });
                state.acceptedFiles = acceptedFiles
                    .filter(function (_a) {
                    var name = _a.name, alreadySubmitted = _a.alreadySubmitted;
                    return submittedFiles.includes(name) || alreadySubmitted;
                })
                    .map(function (acceptedFile) { return createAcceptedFile(acceptedFile, true); });
                state.rejectedFilesErrors = Object.values(getRejectedFilesErrors(state.rejectedFiles));
                state.currentView = AdditionalInfoView.Upload;
                triggerAnalyticsEvent({
                    pageKind: Analytics.Dispute,
                    pageFunction: Analytics.AdditionalInfo,
                    pageSubFunction: Analytics.Upload_Documents,
                    pageName: Analytics.Error,
                    errorMessage: state.apiError
                });
            }
            else {
                state.currentView = AdditionalInfoView.SubmitConfirmation;
                var eventOptions = getAdditionalInfoAnalyticsEventOptions(state.currentView, acceptedFiles, userComment);
                triggerAnalyticsEvent(eventOptions);
            }
        });
    }
});
export var clearAdditionalInfoState = (_a = additionalInfo.actions, _a.clearAdditionalInfoState), updateSelectedFile = _a.updateSelectedFile, deleteSelectedFile = _a.deleteSelectedFile, updateAcceptedFiles = _a.updateAcceptedFiles, updateAdditionalInfoView = _a.updateAdditionalInfoView, updateRejectedFiles = _a.updateRejectedFiles, updateUserComment = _a.updateUserComment;
export default additionalInfo.reducer;
//# sourceMappingURL=additionalInfo.slice.js.map